import { useCallback, useEffect, useRef } from 'react';
import { deepClone, deepEqual, get, type Nullable } from '@direct-frontend/stdlib';
import { useFormContext, useFormState, useFormValue } from '@adv-frontend/form-kit';
import { updateOrder } from '@/entities/order';
import { modalsService } from '@/shared/lib/modalsService';
import { ORDER_FORM_ID } from '../../constants';
import { orderForm, type OrderFormValues } from '../../model/forms/orderForm';
import { fromFormValues } from '../mappers/fromFormValues';
import { reinitializeOrderForm } from '../reinitializeOrderForm';

const isFormSubmittingError = (error: unknown) => get(error, ['name']) === 'FormIsSubmittingError';

export const useOrderFormUpdateReaction = () => {
    const values = useFormValue(orderForm, ORDER_FORM_ID);
    const { orderId, signature, storeSlug, order } = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const { isSubmitting } = useFormState({ form: orderForm, id: ORDER_FORM_ID });

    const lastValuesForUpdate = useRef<Nullable<OrderFormValues>>(null);
    const isUpdating = useRef<boolean>(false);

    const handleUpdateOrder = useCallback(async () => {
        if (isUpdating.current || isSubmitting) {
            return;
        }

        if (!lastValuesForUpdate.current) {
            lastValuesForUpdate.current = deepClone(values);

            return;
        }

        if (deepEqual(lastValuesForUpdate.current, values)) {
            return;
        }

        const orderMeta = fromFormValues(orderId, values, order);

        if (!orderMeta) {
            return;
        }

        isUpdating.current = true;

        lastValuesForUpdate.current = deepClone(values);

        try {
            const updatedOrder = await updateOrder({
                orderMeta,
                signature,
                storeSlug,
            });

            reinitializeOrderForm(order, updatedOrder);
        } catch (error) {
            //  При изменении какого либо значения, инициализурующего обновление ордера и запрос опций, и быстром переходе к оплате
            //  падают ошибки, никак не влияющие на флоу заказа - игнорируем их
            if (isFormSubmittingError(error)) {
                return;
            }

            modalsService.onUpdateOrderFailure();
        } finally {
            isUpdating.current = false;
        }
    }, [isSubmitting, order, orderId, signature, storeSlug, values]);

    useEffect(() => {
        handleUpdateOrder();
    }, [handleUpdateOrder]);
};
